import React, { useCallback, useEffect } from 'react';
import FileUpload from '../../../../components/FileUpload';
import ImportService from '../../../../services/import-service';
import PQueue from 'p-queue/dist/index.js';
import { Card, Text, Stack, Icon, Shelf } from '@chaser/components';
import { toast } from '../../../../components/ToastContainer';

const BATCH_SIZE = 10;

function prepareError(processingFailed, matchingFailed, uploadFailed) {
  const rejectedNotProcessed = [
    ...processingFailed.current.map(name => ({
      name,
      errors: 'Processing error occured.',
    })),
  ];
  const rejectedNotMatched = [
    ...matchingFailed.current.map(name => ({
      name,
      errors: 'Not matched with any Invoice ID.',
    })),
  ];
  const rejectedNotUploaded = [
    // overwrite errors with short, generic one
    ...uploadFailed.map(({ name }) => ({ name, errors: 'Upload failed.' })),
  ];

  let message;
  let rejectedFiles;

  if (
    rejectedNotMatched.length > 0 &&
    rejectedNotUploaded.length === 0 &&
    rejectedNotProcessed.length === 0
  ) {
    message =
      'The following files could not be matched to an invoice. Please update the name of the file to the Invoice ID and then reupload the file.';
    rejectedFiles = rejectedNotMatched.map(({ name }) => ({ name })); // discard individual errors
  } else if (
    rejectedNotUploaded.length > 0 &&
    rejectedNotProcessed.length === 0 &&
    rejectedNotMatched.length === 0
  ) {
    message = 'Upload failed for some files, please reupload.';
    rejectedFiles = rejectedNotUploaded.map(({ name }) => ({ name })); // discard individual errors
  } else if (
    rejectedNotProcessed.length > 0 &&
    rejectedNotUploaded.length === 0 &&
    rejectedNotMatched.length === 0
  ) {
    message = 'Processing failed for some files, please review them before reuploading.';
    rejectedFiles = rejectedNotProcessed.map(({ name }) => ({ name })); // discard individual errors
  } else {
    message =
      'The following files could not be uploaded, matched to an invoice or processed. Please update the name of the file to the Invoice ID where needed and then reupload the file.';
    rejectedFiles = [...rejectedNotProcessed, ...rejectedNotMatched, ...rejectedNotUploaded];
  }
  return { message, rejectedFiles };
}

const PDFUpload = ({ shortCode, maxFileSize }) => {
  const filesToProcess = React.useRef([]);
  const successfullyProcessed = React.useRef([]);
  const matchingFailed = React.useRef([]);
  const processingFailed = React.useRef([]);

  const [abortController] = React.useState(() => new AbortController());
  const [queue] = React.useState(() => new PQueue({ concurrency: 1 }));

  const onBeforeUpload = useCallback(() => {
    filesToProcess.current = [];
    successfullyProcessed.current = [];
    matchingFailed.current = [];
    processingFailed.current = [];
  }, []);

  const processBatch = useCallback(
    async filesBatch => {
      const prefix = filesBatch[0].prefix;
      const names = filesBatch.map(f => f.name);

      return queue
        .add(async () => ImportService.importPDFs(shortCode, prefix, names), {
          signal: abortController.signal,
        })
        .then(() => {
          successfullyProcessed.current.push(...names);
        })
        .catch(err => {
          if (err.code === 'CSV_MATCHING_ERROR') {
            matchingFailed.current.push(...err.meta.errors.map(({ filename }) => filename));
          } else {
            // can happen if e.g. aborted or network/server error occurs
            processingFailed.current.push(...names);
          }
        });
    },
    [shortCode, queue, abortController],
  );

  const onFileUploadSuccess = useCallback(
    ({ name, prefix }) => {
      filesToProcess.current.push({ name, prefix });

      if (filesToProcess.current.length >= BATCH_SIZE) {
        const batch = filesToProcess.current.splice(0, BATCH_SIZE);
        void processBatch(batch);
      }
    },
    [processBatch],
  );

  const onAllFilesUploaded = useCallback(
    async (successes, failures) => {
      if (filesToProcess.current.length > 0) {
        // there are still files to process, process them all in one last batch
        const lastBatch = [...filesToProcess.current];
        filesToProcess.current = [];
        void processBatch(lastBatch);
      }

      abortController.signal.throwIfAborted();

      await queue.onIdle();

      abortController.signal.throwIfAborted();

      if (successes.length > 0 && successes.length === successfullyProcessed.current.length) {
        toast.success('Upload successful');
        return;
      }

      let { message, rejectedFiles } = prepareError(processingFailed, matchingFailed, failures);

      const err = new Error(message);
      err.rejectedFiles = rejectedFiles;
      err.expose = true;
      throw err;
    },
    [abortController, processBatch, queue],
  );

  useEffect(() => {
    const controller = abortController;
    return () => controller.abort();
  }, [abortController]);

  return (
    <Stack gap="medium">
      <Card
        appearance="secondary"
        p="small"
        backgroundColor="Light Neutral 2"
        borderColor="transparent"
      >
        <Shelf gap="small">
          <Icon name="alert-square" />
          <Stack gap="xsmall">
            <Text fontWeight="bold">
              Invoice PDFs are linked to the corresponding invoices using the Invoice ID.
            </Text>
          </Stack>
        </Shelf>
      </Card>

      <FileUpload
        accept={{ 'application/pdf': ['.pdf'] }}
        maxSize={maxFileSize}
        multiple={true}
        getUploadInfo={ImportService.getPDFUploadLinks.bind(null, shortCode)}
        onBeforeUpload={onBeforeUpload}
        onFileUploadSuccess={onFileUploadSuccess}
        onAllFilesUploaded={onAllFilesUploaded}
        signal={abortController.signal}
        showUploadStatus={true}
      />
    </Stack>
  );
};

export default PDFUpload;
